"use client";

import { useState, useEffect, useCallback, useRef } from "react";

const DEFAULT_SCROLL_THRESHOLD = 10;
const DEFAULT_TOP_THRESHOLD = 50;

/**
 * Hook that hides an element when scrolling down and shows it when scrolling up.
 * Used for bottom navigation bars and floating action bars.
 */
export function useScrollVisibility({
  scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
  topThreshold = DEFAULT_TOP_THRESHOLD,
}: { scrollThreshold?: number; topThreshold?: number } = {}) {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - lastScrollY.current;

        if (Math.abs(scrollDelta) > scrollThreshold) {
          if (scrollDelta > 0 && currentScrollY > topThreshold) {
            setIsVisible(false);
          } else if (scrollDelta < 0) {
            setIsVisible(true);
          }
          lastScrollY.current = currentScrollY;
        }

        if (currentScrollY <= 10) {
          setIsVisible(true);
        }

        ticking.current = false;
      });
      ticking.current = true;
    }
  }, [scrollThreshold, topThreshold]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return isVisible;
}
